import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useGetAccounts, { ExternalAccount } from "hooks/useGetAccounts";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { GET_SUPPORTED_INTEGRATIONS } from "../../graphql/integration";
import { IntegrationAction } from "../../enums/IntegrationAction";
import Role from "enums/Role";
import { ConfirmDialog } from "@outpave/ds";

/**
 * Prompts an admin to reconnect any integration whose account has dropped.
 *
 * Lifted out of the admin navbar when that moved onto the shared `AppNavbar`.
 * It never was navbar chrome: it polls integrations, watches for disconnected
 * accounts and raises a modal. The navbar was only where it happened to be
 * mounted, which is why it also had to check it was not on a platform route.
 * Rendered by `LayoutAdmin`, so that check is no longer needed.
 */
const IntegrationDisconnectedPrompt: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);

  const { data: supportedIntegrations } = useQuery(GET_SUPPORTED_INTEGRATIONS, {
    fetchPolicy: "cache-and-network",
    skip: !isAdminOrHigher,
  });

  const integrationAccountTypes = useMemo(
    () =>
      (supportedIntegrations?.integrations || []).map(
        (connection: { integrationType: string }) => connection.integrationType,
      ),
    [supportedIntegrations],
  );

  const { disconnectedAccounts } = useGetAccounts({
    accountTypes: integrationAccountTypes,
    skip: !isAdminOrHigher || !integrationAccountTypes.length,
  });

  const disconnectedNames = useMemo(() => {
    const titleByType = new Map<string, string>(
      (supportedIntegrations?.integrations || []).map(
        (connection: { integrationType: string; title: string }) => [
          connection.integrationType,
          connection.title,
        ],
      ),
    );
    return Array.from(
      new Set(
        disconnectedAccounts.map(
          (account: ExternalAccount) =>
            titleByType.get(account.accountType) || account.name,
        ),
      ),
    );
  }, [disconnectedAccounts, supportedIntegrations]);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (
      !location.pathname.toLowerCase().includes("platform") &&
      disconnectedAccounts.length > 0
    ) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnectedAccounts]);

  function handleReconnect() {
    const base = "/admin/settings/integrations";
    // With exactly one disconnected account we know which integration to reconnect, so deep-link
    // to auto-open its reconnect flow. With 2+ we can't tell which, so just land on the page.
    if (disconnectedAccounts.length === 1) {
      const type = disconnectedAccounts[0].accountType;
      navigate(
        `${base}?integrationType=${type}&action=${IntegrationAction.RECONNECT}`,
      );
    } else {
      navigate(base);
    }
    setOpen(false);
  }

  const nameList =
    disconnectedNames.length <= 1
      ? disconnectedNames.join("")
      : `${disconnectedNames.slice(0, -1).join(", ")} ${t("and")} ${disconnectedNames.slice(-1)}`;

  const description =
    disconnectedNames.length > 1
      ? t("integrationDisconnectedPlural", {
          names: nameList,
          // Integration titles can contain "&", "<", etc. i18next escapes interpolated values by
          // default; disable it here since React escapes the text node when rendering (no XSS risk).
          interpolation: { escapeValue: false },
        })
      : t("integrationDisconnectedSingular", {
          name: nameList,
          interpolation: { escapeValue: false },
        });

  return (
    <ConfirmDialog
      open={open}
      tone="primary"
      title={t("integrationDisconnectedTitle")}
      message={description}
      confirmLabel={t("Reconnect")}
      cancelLabel={t("Later")}
      onConfirm={handleReconnect}
      onCancel={() => setOpen(false)}
    />
  );
};

export default IntegrationDisconnectedPrompt;
